import { BettingState, LEG_TYPES, PRICE_TYPES } from "@ppb/betslip-core";
import { RunnersMetadata } from "@ppb/tbd-store/state/betting/sportsbook-betting/SportsbookBetting.types";
import { SportsbookOdds } from "@ppb/tbd-store/state/entities/SportsbookOdds.types";
import { hasAnyMarketClosedFailure, hasAnyMarketSuspendedFailure } from "@ppb/tbd-store/helpers/sportsbook-betting";
import { HintType } from "@ppb/the-wall-common/types";
import { i18n } from "../../helpers/i18n";

type SingleLegExtraDetails = {
  previousOdds?: SportsbookOdds;
  hasFailure: boolean;
  eachWaySubtitle?: string;
  hintType?: HintType;
  hintMessage?: string;
  isClosed: boolean;
};

export type BetOptions = {
  hasEachWay: boolean;
  hasStartingPrice: boolean;
  isEachWaySelected: boolean;
  isStartingPriceSelected: boolean;
  hasSomeStartingPriceLegSelected: boolean;
  multiplier?: string;
};

const isLegStartingPrice = (leg: BettingState.Leg) => !leg.odds && leg.legType !== LEG_TYPES.ONE_LINE_BET;

export const buildBetOptions = (
  combination: BettingState.Combination,
  legs: BettingState.LegsMap,
  runners: BettingState.RunnersMap,
): BetOptions => {
  const hasAllStartingPriceLegSelected = combination.legs.every((legId) => isLegStartingPrice(legs[legId]));
  const hasSomeStartingPriceLegSelected = combination.legs.some((legId) => isLegStartingPrice(legs[legId]));
  const hasStartingPriceLegAvailable = combination.legs
    .flatMap((legId) => legs[legId].runners)
    .every((runnerId) => runners[runnerId].availablePriceTypes?.includes(PRICE_TYPES.STARTING_PRICE));

  return {
    hasEachWay: !!combination.isEachWayAvailable,
    hasStartingPrice: hasStartingPriceLegAvailable && !!combination.odds && combination.numLines === 1,
    isEachWaySelected: !!combination.isEachWaySelected,
    isStartingPriceSelected: !!combination.isSPSelected || hasAllStartingPriceLegSelected,
    hasSomeStartingPriceLegSelected,
    multiplier: combination.isEachWaySelected ? "2x" : undefined,
  };
};

export const buildEachWaySubtitle = (combination: BettingState.Combination): string | undefined =>
  combination.isEachWayAvailable
    ? i18n({
        key: "I18N.BETSLIP.EACHWAY_ODDS_PLACES",
        interpolationValues: {
          numerator: combination.eachWayPlacesFraction?.numerator || "",
          denominator: combination.eachWayPlacesFraction?.denominator || "",
          places: combination.eachWayPlaces || "",
        },
      })
    : undefined;

const getMarketFailureCode = (runnerFailures: BettingState.ImplyRunnerFailure[]): string | undefined => {
  if (hasAnyMarketClosedFailure(runnerFailures)) {
    return "CLOSED";
  }

  if (hasAnyMarketSuspendedFailure(runnerFailures)) {
    return "SUSPENDED";
  }

  return undefined;
};

export const buildSingleLegExtraDetails = (
  combination: BettingState.Combination,
  legs: BettingState.LegsMap,
  failures: BettingState.ImplyFailureGroup,
  metadata: RunnersMetadata,
): SingleLegExtraDetails => {
  const [legId] = combination.legs;
  const [runnerId] = legs[legId].runners;
  const runnerMetadata = metadata[runnerId];
  const [firstPreviousOdds] = runnerMetadata?.previousOdds || [];
  const runnerFailure = failures.runners[runnerId];
  const failureCode = getMarketFailureCode(runnerFailure || []);
  const isClosed = hasAnyMarketClosedFailure(runnerFailure || []);
  const hasFailure = !!failureCode;

  return {
    previousOdds: firstPreviousOdds,
    hasFailure,
    isClosed,
    eachWaySubtitle: buildEachWaySubtitle(combination),
    hintType: hasFailure ? HintType.Warning : undefined,
    hintMessage: hasFailure ? i18n({ key: `I18N.MARKET.${failureCode}` }) : undefined,
  };
};
